import React, { useState, useRef, useEffect } from "react";

const SymptomSelector = ({ label, categories, selected, setSelected }) => {
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [addingCustom, setAddingCustom] = useState(false);
  const [customSymptom, setCustomSymptom] = useState("");
  const dropdownRef = useRef(null);

  const selectedSymptoms = Array.isArray(selected) ? selected : [];
  const allSymptoms = Array.isArray(categories) ? categories : [];

  // Filter out already selected symptoms
  const filteredSymptoms = allSymptoms.filter(
    (symptom) =>
      !selectedSymptoms.includes(symptom) &&
      (!inputValue || symptom.toLowerCase().includes(inputValue.toLowerCase()))
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleAdd = (symptom) => {
    if (symptom && !selectedSymptoms.includes(symptom)) {
      setSelected([...selectedSymptoms, symptom]);
      setInputValue("");
      setShowDropdown(false);
    }
  };

  const handleAddCustom = () => {
    const normalized = customSymptom.trim();
    if (normalized && !selectedSymptoms.includes(normalized)) {
      setSelected([...selectedSymptoms, normalized]);
    }
    setCustomSymptom("");
    setAddingCustom(false);
    setShowDropdown(false);
  };

  return (
    <div className="tw-mb-6">
      <label className="tw-block tw-mb-2">{label}</label>
      <div className="tw-flex tw-gap-2 tw-mb-2">
        <div className="tw-relative tw-w-full" ref={dropdownRef}>
          <input
            type="text"
            placeholder="Search or select a symptom"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowDropdown(true);
              setAddingCustom(false);
            }}
            onFocus={() => {
              setShowDropdown(true);
              setAddingCustom(false);
            }}
            className="tw-border tw-border-gray-300 tw-rounded-lg tw-px-4 tw-py-3 tw-w-full dark:tw-bg-black"
          />
          {showDropdown && (
            <div className="tw-absolute tw-z-10 tw-w-full tw-bg-white dark:tw-bg-black tw-border tw-border-gray-300 tw-rounded-xl tw-overflow-hidden tw-flex tw-flex-col tw-max-h-80">
              <div className="tw-overflow-auto tw-max-h-80">
                {filteredSymptoms.map((option) => (
                  <div
                    key={option}
                    className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700"
                    onClick={() => handleAdd(option)}
                  >
                    {option}
                  </div>
                ))}
              </div>
              <div className="tw-sticky tw-bottom-0 tw-bg-white dark:tw-bg-black tw-border-t tw-border-gray-200 tw-px-4 tw-py-3">
                <button
                  className="tw-text-blue-800 tw-text-base"
                  onClick={() => {
                    setAddingCustom(true);
                    setShowDropdown(false);
                  }}
                >
                  + <span className="hover:tw-underline">Add your own</span>
                </button>
              </div>
            </div>
          )}
          {inputValue && (
            <button
              type="button"
              onClick={() => setInputValue("")}
              className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pr-2 hover:tw-text-gray-600"
              aria-label="Clear input"
            >
              ×
            </button>
          )}
        </div>
      </div>
      {addingCustom && (
        <div className="tw-flex tw-gap-2 tw-mb-2">
          <input
            type="text"
            className="tw-border tw-rounded-lg tw-p-4 tw-w-full"
            placeholder="Enter custom symptom"
            value={customSymptom}
            onChange={(e) => setCustomSymptom(e.target.value)}
          />
          <button
            type="button"
            onClick={handleAddCustom}
            className={`tw-px-5 tw-rounded-lg tw-min-h-[50px] tw-text-sm ${
              customSymptom.trim()
                ? "tw-bg-green-500 tw-text-white hover:tw-bg-green-600"
                : "tw-bg-gray-300 tw-text-gray-500 tw-cursor-not-allowed"
            }`}
            disabled={!customSymptom.trim()}
          >
            Add
          </button>
        </div>
      )}
      {selectedSymptoms.length > 0 && (
        <div className="tw-mt-3 tw-mb-6">
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {selectedSymptoms.map((symptom) => (
              <div
                key={symptom}
                className="tw-flex tw-items-center tw-bg-black tw-text-white tw-text-sm lg:tw-text-base tw-px-3 lg:tw-px-5 tw-py-2 tw-rounded-full"
              >
                {symptom}
                <button
                  type="button"
                  onClick={() => {
                    const newSelection = selectedSymptoms.filter((s) => s !== symptom);
                    setSelected(newSelection);
                  }}
                  className="tw-ml-2 tw-text-white hover:tw-text-gray-300"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SymptomSelector;
